import axios from "axios";

/**
 * @description Calls the backend login endpoint with the Cognito JWT so the server can set the
 * session cookie. On success, persists `rails_token` in localStorage for the SPA (shape: `{ data: { jwt, name, email, ... } }`).
 *
 * @param {string} URL — server login endpoint (e.g. `/api/users/login`)
 * @param {() => void | Promise<void>} postLoginTransition — run after token is stored
 * @param {string} cognitoJWT — Cognito id token
 * @param {string} [refreshToken] — optional Cognito refresh token (web login)
 */
export async function decodeJWTTokenInServer(URL, postLoginTransition, cognitoJWT, refreshToken) {
    try {
        const response = await axios.get(URL, {
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + cognitoJWT,
            },
            params: {
                web: true,
                ...(refreshToken != null && refreshToken !== "" ? { refresh_token: refreshToken } : {}),
            },
            withCredentials: true,
        });

        try {
            if (typeof window !== "undefined" && response?.data?.jwt) {
                const d = response.data;
                window.localStorage.setItem(
                    "rails_token",
                    JSON.stringify({
                        data: {
                            jwt: d.jwt,
                            name: d.name ?? "",
                            email: d.email ?? "",
                            user_id: d.user_id,
                            last_admin_path: d.last_admin_path,
                            last_path: d.last_path,
                            last_layout: d.last_layout,
                            language: d.language,
                            dark_mode: d.dark_mode,
                            show_sidebar: d.show_sidebar,
                            context: d.context,
                            expiry: d.expiry,
                            success: d.success,
                        },
                    }),
                );
                window.localStorage.setItem("rails_token_received", String(Date.now()));
            }
        } catch {
            /* ignore storage errors */
        }

        await Promise.resolve(postLoginTransition());
    } catch (e) {
        console.error(e);
        throw e;
    }
}

/**
 * Shown when sign-in fails because the account is disabled. It deliberately says nothing
 * about *why* authentication failed and points the user at their administrator.
 */
export const DISABLED_ACCOUNT_MESSAGE =
    "Unable to authenticate your account. If the problem persists, please contact your administrator or support team.";

/**
 * @description True when a sign-in failure is caused by a disabled account rather than by bad
 * credentials, so the login form can show {@link DISABLED_ACCOUNT_MESSAGE} instead of the
 * wrong-password message. A disabled account fails in one of two places:
 *  - Cognito refuses the user before the password is checked: `NotAuthorizedException` with the
 *    message "User is disabled." (a wrong password is the *same* exception with
 *    "Incorrect username or password.", so the message text is the only discriminator);
 *  - Cognito accepts the password but the portal refuses the account: `/api/users/login` answers
 *    401 `{ error: "account_disabled" }` (`Api::BaseController#reject_disabled_account`).
 *
 * @param {unknown} error — whatever `Auth.signIn`, `Auth.confirmSignIn` or `decodeJWTTokenInServer` threw
 * @returns {boolean}
 */
export function isDisabledAccountError(error) {
    if (!error || typeof error !== "object") return false;

    const response = error.response;
    if (response?.status === 401 && response?.data?.error === "account_disabled") return true;

    const isNotAuthorized = error.name === "NotAuthorizedException" || error.code === "NotAuthorizedException";
    return isNotAuthorized && /disabled/i.test(String(error.message ?? ""));
}
